<template>
  <div class="my-listings-container w-full">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-white mb-2">Moji oglasi</h1>
      <p class="text-gray-400">
        Pregledajte i upravljajte svojim aktivnim i neaktivnim oglasima
      </p>
    </div>

    <!-- Create new listing button -->
    <div class="mb-6">
      <router-link
        to="/create-product"
        class="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 mr-2"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
            clip-rule="evenodd"
          />
        </svg>
        Dodaj novi oglas
      </router-link>
    </div>

    <!-- Custom tab implementation -->
    <div class="w-full">
      <!-- Tab selection -->
      <div class="flex border-b border-gray-700 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="setActiveTab(tab.id)"
          class="px-6 py-3 text-sm font-medium transition-colors relative -mb-px"
          :class="[
            activeTab === tab.id
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-gray-300',
          ]"
        >
          {{ tab.label }}
          <span
            v-if="tab.count !== undefined"
            class="ml-2 px-2 py-0.5 text-xs rounded-full"
            :class="
              activeTab === tab.id
                ? 'bg-blue-900 text-blue-200'
                : 'bg-gray-700 text-gray-300'
            "
          >
            {{ tab.count }}
          </span>
        </button>
      </div>

      <!-- SAMO AKTIVNI TAB SE RENDERUJE -->
      <!-- Active products tab content -->
      <div v-if="activeTab === 'active'" class="w-full">
        <div v-if="showActiveLoader" class="py-12 flex justify-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"
          ></div>
        </div>

        <div v-else-if="errorActive" class="py-12 text-center">
          <div class="text-red-400 mb-4">{{ errorActive }}</div>
          <button
            @click="fetchActiveProducts(activeCurrentPage)"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Pokušaj ponovo
          </button>
        </div>

        <div
          v-else-if="activeProducts.length === 0"
          class="py-12 text-center text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-16 w-16 mx-auto mb-4 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
          <p class="text-lg font-medium mb-2">Nemate aktivnih oglasa</p>
          <p class="mb-4">Kreirajte novi oglas da biste počeli sa prodajom</p>
          <router-link
            to="/create-product"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            Dodaj novi oglas
          </router-link>
        </div>

        <div v-else class="space-y-6">
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-6xl mx-auto"
          >
            <div
              v-for="product in activeProducts"
              :key="product.id"
              class="relative"
            >
              <!-- Product Card -->
              <div class="absolute top-2 right-2 z-10">
                <ProductManageActions
                  :is-active="true"
                  @edit="navigateToEdit(product.id)"
                  @delete="openDeleteModal(product, true)"
                />
              </div>
              <ProductCard :product="product" :show-follow-button="false" />
            </div>
          </div>

          <!-- Paginacija SAMO za aktivne oglase -->
          <PaginationComponent
            :meta="activeMeta"
            :current-page="activeCurrentPage"
            :update-url="true"
            @page-change="handleActivePageChange"
          />
        </div>
      </div>

      <!-- Inactive products tab content -->
      <div v-if="activeTab === 'inactive'" class="w-full">
        <!-- Loading za statistike -->
        <div v-if="isLoadingStats" class="mb-6">
          <div
            class="bg-gray-800 rounded-lg p-4 flex items-center justify-center"
          >
            <div
              class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-3"
            ></div>
            <span class="text-gray-400"
              >Proverava se broj aktivnih oglasa...</span
            >
          </div>
        </div>

        <!-- Upozorenja o limitu za obnavljanje oglasa -->
        <div v-if="!isLoadingStats" class="mb-6">
          <!-- BLOKIRANJE - 0 preostalih oglasa -->
          <div
            v-if="userStats.remainingSlots === 0"
            class="bg-red-900/30 border border-red-500 rounded-lg p-4 mb-6"
          >
            <div class="flex items-start">
              <svg
                class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <div>
                <h3 class="text-red-400 font-medium mb-1">
                  Dostigli ste maksimalan broj aktivnih besplatnih oglasa
                </h3>
                <p class="text-red-300 text-sm">
                  Trenutno imate
                  <strong>{{ userStats.activeProductsCount }}</strong> od
                  <strong>{{ userStats.maxProductsAllowed }}</strong>
                  dozvoljenih aktivnih besplatnih oglasa. Da biste obnovili neki
                  oglas, sačekajte da neki postojeći istekne ili ga ručno
                  obrišite.
                </p>
              </div>
            </div>
          </div>

          <!-- CRVENO UPOZORENJE - manje od 20 preostalih oglasa (ali ne 0) -->
          <div
            v-else-if="
              userStats.remainingSlots > 0 && userStats.remainingSlots < 20
            "
            class="bg-red-900/30 border border-red-500 rounded-lg p-4 mb-6"
          >
            <div class="flex items-start">
              <svg
                class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <div>
                <h3 class="text-red-400 font-medium mb-1">
                  Kritično malo preostalih besplatnih oglasa
                </h3>
                <p class="text-red-300 text-sm">
                  Možete obnoviti još samo
                  <strong>{{ userStats.remainingSlots }}</strong> oglasa.
                  Trenutno imate
                  <strong>{{ userStats.activeProductsCount }}</strong> aktivnih
                  besplatnih oglasa.
                </p>
              </div>
            </div>
          </div>

          <!-- ŽUTO UPOZORENJE - između 20 i 39 preostalih oglasa -->
          <div
            v-else-if="
              userStats.remainingSlots >= 20 && userStats.remainingSlots < 40
            "
            class="bg-yellow-900/30 border border-yellow-500 rounded-lg p-4 mb-6"
          >
            <div class="flex items-start">
              <svg
                class="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <div>
                <h3 class="text-yellow-400 font-medium mb-1">
                  Približavate se limitu maksimalnog broja besplatnih oglasa
                </h3>
                <p class="text-yellow-300 text-sm">
                  Možete obnoviti još
                  <strong>{{ userStats.remainingSlots }}</strong> oglasa.
                  Trenutno imate
                  <strong>{{ userStats.activeProductsCount }}</strong> aktivnih
                  besplatnih oglasa.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showInactiveLoader" class="py-12 flex justify-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"
          ></div>
        </div>

        <div v-else-if="errorInactive" class="py-12 text-center">
          <div class="text-red-400 mb-4">{{ errorInactive }}</div>
          <button
            @click="fetchInactiveProducts(inactiveCurrentPage)"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Pokušaj ponovo
          </button>
        </div>

        <div
          v-else-if="inactiveProducts.length === 0"
          class="py-12 text-center text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-16 w-16 mx-auto mb-4 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
            />
          </svg>
          <p class="text-lg font-medium mb-2">Nemate neaktivnih oglasa</p>
          <p>
            Ovde će se prikazati vaši oglasi kojima je istekao period aktivnosti
          </p>
        </div>

        <div v-else class="space-y-6">
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-6xl mx-auto"
          >
            <div
              v-for="product in inactiveProducts"
              :key="product.id"
              class="relative"
            >
              <!-- Product Card -->
              <div class="absolute top-2 right-2 z-10">
                <ProductManageActions
                  :is-active="false"
                  :can-renew="userStats.remainingSlots > 0"
                  @renew="openRenewModal(product)"
                  @delete="openDeleteModal(product, false)"
                />
              </div>
              <ProductCard :product="product" :show-follow-button="false" />

              <!-- Expired badge -->
              <div
                class="absolute top-0 left-0 m-2 px-2 py-1 bg-red-900 text-red-200 text-xs font-medium rounded"
              >
                Istekao
              </div>
            </div>
          </div>

          <!-- Paginacija SAMO za neaktivne oglase -->
          <PaginationComponent
            :meta="inactiveMeta"
            :current-page="inactiveCurrentPage"
            :update-url="true"
            @page-change="handleInactivePageChange"
          />
        </div>
      </div>
    </div>

    <!-- Confirmation Modals -->
    <ConfirmationModal
      :show="deleteModal.show"
      :title="deleteModal.title"
      :message="deleteModal.message"
      type="danger"
      confirm-text="Obriši"
      :is-loading="deleteModal.isLoading"
      @close="deleteModal.show = false"
      @confirm="confirmDelete"
    />

    <ConfirmationModal
      :show="renewModal.show"
      :title="renewModal.title"
      :message="renewModal.message"
      type="success"
      confirm-text="Obnovi"
      :is-loading="renewModal.isLoading"
      @close="renewModal.show = false"
      @confirm="confirmRenew"
    />

    <!-- Toast Notification -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import '@/styles/my-listing-tabs-fix.css';
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ProductCard from '@/components/ProductCard.vue';
import ProductManageActions from '@/components/ProductManageActions.vue';
import ConfirmationModal from '@/components/ConfirmationModal.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import { useMyListings } from '@/composables/useMyListings';
import { debounce } from '@/utils/debounce';

interface Product {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: string;
  stock: number;
  isActive: boolean;
  userId: string;
  mainPicture: string | null;
  category: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  renewedAt: string | null;
  followersCount: number;
  city: string;
  username: string;
}

export default defineComponent({
  name: 'MyListingsView',
  components: {
    ProductCard,
    ProductManageActions,
    ConfirmationModal,
    ToastNotification,
    PaginationComponent,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    // Get active tab from URL query or default to 'active'
    const activeTab = ref((route.query.tab as string) || 'active');

    const showActiveLoader = ref(false);
    const showInactiveLoader = ref(false);

    // Get current pages from URL query
    const activeCurrentPage = computed(() => {
      if (activeTab.value !== 'active') return 1;
      const page = parseInt(route.query.page as string, 10);
      return isNaN(page) || page < 1 ? 1 : page;
    });

    const inactiveCurrentPage = computed(() => {
      if (activeTab.value !== 'inactive') return 1;
      const page = parseInt(route.query.page as string, 10);
      return isNaN(page) || page < 1 ? 1 : page;
    });

    // Use the composable - OVDE JE PROBLEM - trebalo je da dodamo userStats i isLoadingStats u return
    const {
      activeProducts,
      inactiveProducts,
      activeMeta,
      inactiveMeta,
      userStats,
      isLoadingStats,
      isLoadingActive,
      isLoadingInactive,
      errorActive,
      errorInactive,
      toast,
      fetchActiveProducts,
      fetchInactiveProducts,
      deleteProductWithPagination,
      renewProduct,
      loadUserStats,
    } = useMyListings();

    // Debounced funkcije za loader
    const showActiveLoaderFn = () => {
      showActiveLoader.value = true;
    };

    const hideActiveLoaderFn = () => {
      showActiveLoader.value = false;
    };

    const showInactiveLoaderFn = () => {
      showInactiveLoader.value = true;
    };

    const hideInactiveLoaderFn = () => {
      showInactiveLoader.value = false;
    };

    // Debounced funkcije sa kratkim kašnjenjem za sprečavanje treperenja UI-ja
    const debouncedShowActiveLoader = debounce(showActiveLoaderFn, 200);
    const debouncedShowInactiveLoader = debounce(showInactiveLoaderFn, 200);

    // Odmah sakrijemo loader
    const immediateHideActiveLoader = () => {
      debouncedShowActiveLoader.cancel();
      hideActiveLoaderFn();
    };

    const immediateHideInactiveLoader = () => {
      debouncedShowInactiveLoader.cancel();
      hideInactiveLoaderFn();
    };

    // Pratimo stanje učitavanja za aktivne i neaktivne proizvode
    watch(isLoadingActive, (isLoading) => {
      if (isLoading) {
        debouncedShowActiveLoader();
      } else {
        immediateHideActiveLoader();
      }
    });

    watch(isLoadingInactive, (isLoading) => {
      if (isLoading) {
        debouncedShowInactiveLoader();
      } else {
        immediateHideInactiveLoader();
      }
    });

    // Watch for route changes (page parameter)
    watch(
      () => route.query.page,
      (newPage) => {
        if (activeTab.value === 'active') {
          const page = parseInt(newPage as string, 10) || 1;
          fetchActiveProducts(page);
        } else if (activeTab.value === 'inactive') {
          const page = parseInt(newPage as string, 10) || 1;
          fetchInactiveProducts(page);
        }
      }
    );

    // Watch for tab changes in URL
    watch(
      () => route.query.tab,
      (newTab) => {
        if (newTab && newTab !== activeTab.value) {
          activeTab.value = newTab as string;
          loadTabData(newTab as string);
        }
      }
    );

    // Compute tabs with counts from meta data
    const tabs = computed(() => [
      {
        id: 'active',
        label: 'Aktivni oglasi',
        count: activeMeta.value?.total || 0,
      },
      {
        id: 'inactive',
        label: 'Neaktivni oglasi',
        count: inactiveMeta.value?.total || 0,
      },
    ]);

    // Load data for specific tab
    const loadTabData = (tabId: string) => {
      if (tabId === 'active') {
        fetchActiveProducts(activeCurrentPage.value);
      } else if (tabId === 'inactive') {
        fetchInactiveProducts(inactiveCurrentPage.value);
      }
    };

    // Set active tab and update URL
    const setActiveTab = (tabId: string) => {
      activeTab.value = tabId;

      // Update URL with new tab and reset page to 1
      router.push({
        path: route.path,
        query: { tab: tabId, page: '1' },
      });

      loadTabData(tabId);
    };

    // Pagination handlers - these will be called by PaginationComponent when updateUrl is true
    const handleActivePageChange = (page: number) => {
      console.log('handleActivePageChange called with page:', page);
      // PaginationComponent will handle URL update since updateUrl is true
    };

    const handleInactivePageChange = (page: number) => {
      console.log('handleInactivePageChange called with page:', page);
      // PaginationComponent will handle URL update since updateUrl is true
    };

    // Delete modal state
    const deleteModal = ref({
      show: false,
      title: '',
      message: '',
      isLoading: false,
      product: null as Product | null,
      isActive: false,
    });

    // Renew modal state
    const renewModal = ref({
      show: false,
      title: '',
      message: '',
      isLoading: false,
      product: null as Product | null,
    });

    // Navigation to edit page
    const navigateToEdit = (productId: string) => {
      router.push(`/edit-product/${productId}`);
    };

    // Open delete confirmation modal
    const openDeleteModal = (product: Product, isActive: boolean) => {
      deleteModal.value = {
        show: true,
        title: 'Brisanje oglasa',
        message: `Da li ste sigurni da želite da obrišete oglas "${product.name}"? Ova akcija se ne može poništiti.`,
        isLoading: false,
        product,
        isActive,
      };
    };

    // Open renew confirmation modal
    const openRenewModal = (product: Product) => {
      renewModal.value = {
        show: true,
        title: 'Obnavljanje oglasa',
        message: `Da li ste sigurni da želite da obnovite oglas "${product.name}"? Oglas će ponovo biti aktivan i vidljiv drugim korisnicima.`,
        isLoading: false,
        product,
      };
    };

    // Confirm delete action with pagination fix
    const confirmDelete = async () => {
      if (!deleteModal.value.product) return;

      deleteModal.value.isLoading = true;

      try {
        // Use the new deleteProductWithPagination function
        await deleteProductWithPagination(
          deleteModal.value.product.id,
          deleteModal.value.isActive,
          deleteModal.value.isActive
            ? activeCurrentPage.value
            : inactiveCurrentPage.value,
          (newPage) => {
            // Update URL when page changes due to delete
            router.push({
              path: route.path,
              query: { ...route.query, page: newPage.toString() },
            });
          }
        );

        deleteModal.value.show = false;
      } catch (error) {
        console.error('Error during delete confirmation:', error);
      } finally {
        deleteModal.value.isLoading = false;
      }
    };

    // Confirm renew action
    const confirmRenew = async () => {
      if (!renewModal.value.product) return;

      renewModal.value.isLoading = true;

      try {
        await renewProduct(renewModal.value.product.id);
        renewModal.value.show = false;

        // Reload both tabs as product moves from inactive to active
        fetchActiveProducts(activeCurrentPage.value);
        fetchInactiveProducts(inactiveCurrentPage.value);
      } catch (error) {
        console.error('Error during renew confirmation:', error);
      } finally {
        renewModal.value.isLoading = false;
      }
    };

    // Load data on component mount
    onMounted(async () => {
      // Load user stats first
      await loadUserStats();
      // Initial fetch based on current tab and page from URL
      loadTabData(activeTab.value);
    });

    // Clean up on component unmount
    onBeforeUnmount(() => {
      debouncedShowActiveLoader.cancel();
      debouncedShowInactiveLoader.cancel();
    });

    return {
      activeTab,
      tabs,
      activeProducts,
      inactiveProducts,
      activeMeta,
      inactiveMeta,
      userStats, // DODANO
      isLoadingStats, // DODANO
      activeCurrentPage,
      inactiveCurrentPage,
      isLoadingActive,
      isLoadingInactive,
      errorActive,
      errorInactive,
      toast,
      deleteModal,
      renewModal,
      setActiveTab,
      handleActivePageChange,
      handleInactivePageChange,
      navigateToEdit,
      openDeleteModal,
      openRenewModal,
      confirmDelete,
      confirmRenew,
      fetchActiveProducts,
      fetchInactiveProducts,
      showActiveLoader,
      showInactiveLoader,
    };
  },
});
</script>
